import { FC, FocusEventHandler } from "react";
import {
  IncreaseOfShareCapitalType,
  SearchCompanyModel,
  ShareAllocationRoundsModel,
  ShareTypeForSubscribersStatusModel,
  SubscriberAllocationRow,
  SubscriberDialogState,
  SubscriberForIncreaseNominalValueModel,
  SubscriberForIssueModel,
  subscriberSignerLabel,
  SubscribersStatusForIssueModel,
  SubscribersStatusForNominalValueModel,
  SubscribersStatusModel,
  SubscriberStatusIssueRow,
  ViewSubscribersDialogModel,
} from "./increaseOfShareCapitalDomain";
import { observer } from "mobx-react";
import { Box, VBox } from "@/components-v2/Box";
import { Dialog } from "@/components-v2/Dialog";
import { isOk, isPending } from "@/common/common";
import { Tabs } from "@/components-v2/Tabs";
import { DataTable, TableColumn } from "@/components-v2/DataTable";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import AutomatedSignatures from "@/assets/img/automated-signatures.svg";
import { EmptyState } from "@/components-v2/EmptyState";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import { Table } from "@/components-v2/Table";
import { MTextInput } from "@/components-v2/TextInput";
import { percentOf } from "@/utils/numbers";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { isMobileView } from "@/utils/screen";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { preventDefault } from "@/utils/events";
import { Tag } from "@/components-v2/Tag";
import { compact } from "lodash";
import { CURRENCY_NOK } from "@/utils/currency";
import { when } from "@/utils/functions";
import { useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { NumberOfSharesForShareType } from "@/dbot/common/commonDomain";
import { StringFieldModel } from "@/common/commonForms";
import { findShareTypeName, lookupShareTypeName, ShareType } from "@/common/share";
import { SelectActorsDialog } from "@/dialogs/dialogComponents";
import { AddContributionInKindDialog } from "./ContributionInKind";
import { AddDebtContributionDialog } from "./DebtContribution";
import { RadioButtonGroup } from "@/components-v2/RadioButton";
import { Alert } from "@/components-v2/Alert";
import { Select } from "@/components-v2/Select";

export const SubscribersStatusDialog: FC<{
  model: ViewSubscribersDialogModel;
}> = observer(({ model }) => {
  switch (model.type) {
    case "status": {
      return <SubscriberStatusDialog model={model.subscriberModel} />;
    }
    case "allocation": {
      return <SubscriberAllocationDialog model={model.subscriberModel} />;
    }
  }
});

const SubscriberStatusDialog = observer(function SubscriberStatusDialog({
  model,
}: {
  model: SubscribersStatusModel;
}) {
  const { t } = useTranslation();
  function render(model: SubscribersStatusModel) {
    switch (model.subscribersModel?.type) {
      case IncreaseOfShareCapitalType.shareIssue:
        return (
          <SubscriberStatusIssuePanel
            model={model.subscribersModel}
            addSignerForAllSubscribers={model.addSignerForAllSubscribers}
            isLocked={model.isLocked}
          />
        );
      case IncreaseOfShareCapitalType.increaseNominalValue:
        return (
          <SubscriberStatusNominalValuePanel
            model={model.subscribersModel}
            addSignerForAllSubscribers={model.addSignerForAllSubscribers}
            isLocked={model.isLocked}
          />
        );
    }
  }

  function renderTabs(model: SubscribersStatusModel) {
    switch (model.subscribersModel?.type) {
      case IncreaseOfShareCapitalType.shareIssue:
        return (
          <SubscriberStatusIssuePanelTabs
            shareTypes={model.subscribersModel.shareTypes}
            selectedShareType={model.subscribersModel.selectedShareType}
            onSelect={model.subscribersModel.setSelectedShareType}
            subscribedShareSumType={model.subscribersModel.subscribedShareSumType}
          />
        );
      case IncreaseOfShareCapitalType.increaseNominalValue: {
        return (
          <SubscriberStatusNominalValuePanelTabs
            numberOfShareForShareTypes={model.subscribersModel.shareTypes}
            selectedShareType={model.subscribersModel.selectedShareType}
            onSelect={model.subscribersModel.setSelectedShareType}
            shareTypes={model.initialState.shareTypes}
          />
        );
      }
    }
  }

  return (
    <Dialog
      fullscreen
      onSubmit={model.saveAndClose}
      onCancel={() => {
        if (model.subscribersModel.isDirty) {
          if (window.confirm(t("subscribers.unsaved-changes-prompt"))) {
            model.close();
          }
        } else {
          model.close();
        }
      }}
      title={t("subscribers.title")}
      subHeaderElement={renderTabs(model)}
    >
      {render(model)}
      {model.subscribersModel.selectedSubscriber && (
        <SubscriberDialog
          selectedSubscriber={model.subscribersModel.selectedSubscriber}
          dialogState={model.subscribersModel.selectedSubscriberDialogState}
          close={() => model.subscribersModel.closeSelectedSubscriberDialog()}
          isLocked={model.isLocked}
          setDirty={model.subscribersModel.setDirty}
        />
      )}
      {model.addSignerForAllSubscribersModel && (
        <AddSignerForAllSubscribersDialog
          model={model.addSignerForAllSubscribersModel}
          submit={model.submitSignerForAllSubscribers}
          close={model.cancelSignerForAllSubscribers}
        />
      )}
    </Dialog>
  );
});

const SubscriberStatusNominalValuePanel = observer(function SubscriberStatusForNominalValueDialog({
  model,
  addSignerForAllSubscribers,
  isLocked,
}: {
  model: SubscribersStatusForNominalValueModel;
  addSignerForAllSubscribers: () => void;
  isLocked: boolean;
}) {
  const { t } = useTranslation();
  return model.shareTypes.length === 0 || model.selectedShareType === undefined ? (
    <VBox grow={"golden"} justifyContent={"center"}>
      <EmptyState
        illustration={<img src={AutomatedSignatures} alt="dCompany" />}
        title={t("decisions.increaseShareCapital.subscribersStatus.emptyShareTypes.header")}
        description={t("decisions.increaseShareCapital.subscribersStatus.emptyShareTypes.info")}
      />
    </VBox>
  ) : (
    <SubscriberStatusNominalValueBody
      model={model}
      addSignerForAllSubscribers={addSignerForAllSubscribers}
      isLocked={isLocked}
    />
  );
});

const SubscriberStatusIssuePanel = observer(function SubscriberStatusForIssueDialog({
  model,
  addSignerForAllSubscribers,
  isLocked,
}: {
  model: SubscribersStatusForIssueModel;
  addSignerForAllSubscribers: () => void;
  isLocked: boolean;
}) {
  const { t } = useTranslation();

  return model.shareTypes.length === 0 || model.selectedShareType === undefined ? (
    <VBox grow={"golden"} justifyContent={"center"}>
      <EmptyState
        illustration={<img src={AutomatedSignatures} alt="dCompany" />}
        title={t("decisions.increaseShareCapital.subscribersStatus.emptyShareTypes.header")}
        description={t("decisions.increaseShareCapital.subscribersStatus.emptyShareTypes.info")}
      />
    </VBox>
  ) : (
    <>
      <SubscriberStatusIssueBody
        model={model}
        selectedShareType={model.selectedShareType}
        addSignerForAllSubscribers={addSignerForAllSubscribers}
        isLocked={isLocked}
      />
      {model.isAddNewShareholderDialog && (
        <AddNewShareholderDialog
          model={model.newSubscriberForIssue}
          searchCompany={model.searchCompany}
          onSubmit={model.submitNewShareholderSubscriber}
          onClose={model.closeAddSubscriberDialog}
        />
      )}
      {model.addShareholdersFromActorsModel && (
        <SelectActorsDialog model={model.addShareholdersFromActorsModel} />
      )}
    </>
  );
});

const SubscriberAllocationDialog = observer(function SubscriberAllocationDialog({
  model,
}: {
  model: ShareAllocationRoundsModel;
}) {
  const { t, lang } = useTranslation();

  return (
    <Dialog
      title={t("decisions.increaseShareCapital.subscribersAllocation.dialogTitle")}
      submitLabel={t("save")}
      onSubmit={model.saveAndClose}
      onCancel={model.close}
      headerElement={
        <Box grow justifyContent={"end"}>
          {model.isEmpty ? undefined : model.isAllocationCompleted ? (
            <LinkButton
              label={t("decisions.increaseShareCapital.subscribersAllocation.continueEditing")}
              loading={isPending(model.saveAllocationResult)}
              onClick={model.toggleAllocationStatusAndSave}
              disabled={model.isSentToBR}
            />
          ) : (
            <Button
              variant={"primary"}
              label={t("decisions.increaseShareCapital.subscribersAllocation.confirmAllocation")}
              loading={isPending(model.saveAllocationResult)}
              disabled={!model.canConfirmAllocation}
              tooltip={
                !model.isAllSharesAllocatedInRounds
                  ? t(
                      "decisions.increaseShareCapital.subscribersAllocation.confirmAllocation.missingShares",
                    )
                  : !model.hasAllocatedValidNumberOfShares
                    ? t(
                        "decisions.increaseShareCapital.subscribersAllocation.confirmAllocation.sharesNotAvailable",
                      )
                    : undefined
              }
              size={"sm"}
              onClick={model.toggleAllocationStatusAndSave}
            />
          )}
        </Box>
      }
      subHeaderElement={
        <Box display={"block"} bg borderBottom>
          <Tabs
            tabs={model.allocationsRounds.map((allocationRound) => ({
              id: allocationRound.id,
              label: findShareTypeName(allocationRound.shareType, lang),
              isActive: allocationRound === model.selectedAllocationRound,
              onSelect: () => model.setSelectedAllocation(allocationRound),
            }))}
            scrollable={false}
          />
        </Box>
      }
      fullscreen
    >
      {isPending(model.allocationResponse) && (
        <VBox grow={"golden"} justifyContent={"center"}>
          <LoadingIndicator />
        </VBox>
      )}
      {isOk(model.allocationResponse) && <SubscriberAllocationPanel model={model} />}
    </Dialog>
  );
});

const SubscriberAllocationPanel = observer(function SubscriberAllocationDialog({
  model,
}: {
  model: ShareAllocationRoundsModel;
}) {
  const { formatNumber, formatPercent } = useLocaleFormat();
  const { t } = useTranslation();

  const tableColumns: TableColumn<SubscriberAllocationRow>[] = [
    {
      name: "title",
      label: t("name"),
      align: "left",
      width: "20%",
      getValue: (entry) =>
        entry.title ?? (
          <strong>
            {t("decisions.increaseShareCapital.subscribersAllocation.totalAllocation", {
              shareType: { type: "shareType", value: model.selectedAllocationRound.shareType },
            })}
          </strong>
        ),
    },
    {
      name: "offeredShares",
      label: t("decisions.increaseShareCapital.subscribersAllocation.offeredShares"),
      width: "10%",
      getValue: (value) => formatNumber(value.offeredShares),
    },
    {
      name: "subscribedShares",
      label: t("decisions.increaseShareCapital.subscribersAllocation.subscribedShares"),
      width: "10%",
      getValue: (value) => formatNumber(value.subscribedShares),
    },
    {
      name: "allocatedShares.parsedValue",
      label: t("decisions.increaseShareCapital.subscribersAllocation.allocatedShares"),
      align: "center",
      width: "5%",
      getValue: (value) => (
        <MTextInput
          model={value.allocatedShares}
          disabled={model.isAllocationCompleted}
          align={"center"}
        />
      ),
    },
    {
      name: "subscriptionPrice",
      label: t("decisions.increaseShareCapital.subscribersAllocation.subscriptionPrice"),
      width: "15%",
      getValue: (value) => formatNumber(value.subscriptionPrice),
    },
    {
      name: "totalPrice",
      label: t("decisions.increaseShareCapital.subscribersAllocation.totalPrice"),
      width: "5%",
      getValue: (value) => formatNumber(value.totalPrice),
    },
    {
      name: "newOwnership",
      label: t("decisions.increaseShareCapital.subscribersAllocation.newOwnership"),
      width: "25%",
      getValue: (value) =>
        `${formatPercent(value.newOwnership)} (${formatPercent(value.currentOwnership)})`,
    },
    {
      name: "newNumberOfShares",
      label: t("decisions.increaseShareCapital.subscribersAllocation.newNumberOfShares"),
      width: "10%",
      getValue: (value) =>
        `${formatNumber(value.newNumberOfShares)} (${formatNumber(value.currentNumberOfShares)})`,
    },
  ];

  if (model.isEmpty) {
    return (
      <VBox grow={"golden"} justifyContent={"center"}>
        <EmptyState
          illustration={<img src={AutomatedSignatures} alt="dCompany" />}
          title={t("decisions.increaseShareCapital.subscribersAllocation.emptyState.header")}
          description={t("decisions.increaseShareCapital.subscribersAllocation.emptyState.info")}
        />
      </VBox>
    );
  }

  return (
    <>
      <VBox padding gap={"lg"} maxWidth={"xl"} container>
        <Box vertical={isMobileView()} justifyContent={"space-between"}>
          <VBox gap={"md"}>
            <h3>
              {t("decisions.increaseShareCapital.subscribersAllocation.title", {
                shareType: { type: "shareType", value: model.selectedAllocationRound.shareType },
              })}
            </h3>
            <AvailableSharesOverview
              isFixed={model.selectedAllocationRound.isSharesFixed}
              minShares={model.selectedAllocationRound.minShares}
              maxShares={model.selectedAllocationRound.maxShares}
              assignedShares={model.selectedAllocationRound.currentAllocatedShares}
              remainingShares={model.selectedAllocationRound.remainingSharesToAllocate}
              totalPrice={model.selectedAllocationRound.totalPrice}
            />
          </VBox>
          <Box gap={"md"}>
            <LinkButton
              label={t(
                "decisions.increaseShareCapital.subscribersAllocation.allocateSubscribedShares",
              )}
              onClick={model.allocateAllOfferedShares}
              disabled={model.isAllocationCompleted}
            />
          </Box>
        </Box>
        <DataTable
          hoverable
          withRowBorders
          tableColumns={tableColumns}
          tableData={model.selectedAllocationRound.toTableData}
          getId={(row) => row.id}
        />
      </VBox>
    </>
  );
});

function SubscriberStatusIssuePanelTab({
  shareType,
  onSelect,
  subscribedShareSumType,
  onFocus,
  className,
}: {
  shareType: ShareTypeForSubscribersStatusModel;
  onSelect: () => void;
  subscribedShareSumType: Record<string, number>;
  onFocus: FocusEventHandler<HTMLElement>;
  className: string;
}) {
  const { lang } = useTranslation();
  const { formatPercent } = useLocaleFormat();
  const minCompletion = percentOf(
    subscribedShareSumType[shareType.sharetypeCode],
    shareType.minNumberOfShares,
  );
  const maxCompletion = percentOf(
    subscribedShareSumType[shareType.sharetypeCode],
    shareType.maxNumberOfShares,
  );
  return (
    <button className={className} onClick={preventDefault(onSelect)} onFocus={onFocus}>
      <Box gap={"sm"}>
        {findShareTypeName(shareType.shareType, lang)}
        {minCompletion < 1 ? (
          <Tag color="info" label={formatPercent(maxCompletion, { maximumFractionDigits: 0 })} />
        ) : maxCompletion > 1 ? (
          <Tag color="error" label={formatPercent(maxCompletion, { maximumFractionDigits: 0 })} />
        ) : (
          <Tag color="success" label={formatPercent(maxCompletion, { maximumFractionDigits: 0 })} />
        )}
      </Box>
    </button>
  );
}

const SubscriberStatusIssuePanelTabs: FC<{
  shareTypes: ShareTypeForSubscribersStatusModel[];
  selectedShareType?: ShareTypeForSubscribersStatusModel;
  onSelect: (shareType: ShareTypeForSubscribersStatusModel) => void;
  subscribedShareSumType: Record<string, number>;
}> = observer(({ shareTypes, selectedShareType, onSelect, subscribedShareSumType }) => {
  const { lang } = useTranslation();

  return (
    <VBox bg border marginTop={"sm"}>
      <Tabs
        tabs={shareTypes.map((shareType) => ({
          id: shareType.sharetypeId.toString(),
          label: findShareTypeName(shareType.shareType, lang),
          render: (props) => (
            <SubscriberStatusIssuePanelTab
              {...props}
              shareType={shareType}
              onSelect={() => onSelect(shareType)}
              subscribedShareSumType={subscribedShareSumType}
            />
          ),
          isActive: shareType === selectedShareType,
          onSelect: () => onSelect(shareType),
        }))}
      />
    </VBox>
  );
});

const SubscriberStatusNominalValuePanelTabs: FC<{
  numberOfShareForShareTypes: NumberOfSharesForShareType[];
  selectedShareType?: NumberOfSharesForShareType;
  onSelect: (shareType: NumberOfSharesForShareType) => void;
  shareTypes: ShareType[];
}> = observer(({ numberOfShareForShareTypes, selectedShareType, onSelect, shareTypes }) => {
  const { lang } = useTranslation();

  return (
    <VBox bg border marginTop={"sm"}>
      <Tabs
        tabs={numberOfShareForShareTypes.map((shareType) => ({
          id: shareType.shareTypeId.toString(),
          label: lookupShareTypeName(shareType.shareTypeId, shareTypes, lang),
          isActive: shareType === selectedShareType,
          onSelect: () => onSelect(shareType),
        }))}
      />
    </VBox>
  );
});

const SubscriberStatusIssueBody: FC<{
  model: SubscribersStatusForIssueModel;
  selectedShareType: ShareTypeForSubscribersStatusModel;
  addSignerForAllSubscribers: () => void;
  isLocked: boolean;
}> = observer(({ model, selectedShareType, addSignerForAllSubscribers, isLocked }) => {
  const { t } = useTranslation();
  const { formatNumber, formatPercent } = useLocaleFormat();

  const tableColumns: TableColumn<SubscriberStatusIssueRow>[] = [
    {
      name: "title",
      label: t("name"),
      align: "left",
      width: "20%",
      notSorted: true,
    },
    {
      name: "numberOfShares",
      label: t(
        model.isPreferentialRights
          ? "decisions.increaseShareCapital.subscribersStatus.subscriptionRights"
          : "decisions.increaseShareCapital.subscribersStatus.subscription",
      ),
      align: "center",
      width: "15%",
    },
    {
      name: "subscriptionPrice",
      label: t("decisions.increaseShareCapital.subscribersStatus.subscriptionPrice"),
      align: "center",
      width: "7%",
    },
    {
      name: "totalPrice",
      label: t("decisions.increaseShareCapital.subscribersStatus.totalPrice"),
      width: "8%",
    },
    {
      name: "currentShares",
      label: t("decisions.increaseShareCapital.subscribersStatus.currentShares"),
      width: "15%",
    },
    {
      name: "currentOwnership",
      label: t("decisions.increaseShareCapital.subscribersStatus.currentOwnership"),
      width: "7%",
    },
    {
      name: "newShares",
      label: t("decisions.increaseShareCapital.subscribersStatus.newShares"),
      width: "7%",
    },
    {
      name: "newOwnership",
      label: t("decisions.increaseShareCapital.subscribersStatus.newOwnership"),
      width: "7%",
    },
    {
      name: "signers",
      label: t("decisions.increaseShareCapital.subscribersStatus.signers"),
      align: "center",
      width: "7%",
    },
    {
      name: "actions",
      label: "",
      width: "7%",
    },
  ];

  const addSubscribersActions = [
    <Button
      key={"add-new-person-subscriber-from-portfolio"}
      onClick={() => model.openAddNewShareholderFromPortfolioDialog()}
      icon={"suitcaseSimple"}
      variant={"secondary"}
      label={t(
        "decisions.increaseShareCapital.subscribersStatus.emptySubscribers.addExistingShareholder",
      )}
    />,
    <Button
      key={"add-new-person-shareholder"}
      onClick={() => model.openAddNewShareholderDialog({ isCompany: false })}
      icon={"plus"}
      variant={"secondary"}
      label={t("subscriber.addBuyer.newPersonalSubscriber")}
    />,
    <Button
      key={"add-new-company-shareholder"}
      onClick={() => model.openAddNewShareholderDialog({ isCompany: true })}
      icon={"plus"}
      variant={"secondary"}
      label={t("subscriber.addBuyer.newCompanySubscriber")}
    />,
  ];

  return model.isSubscribersEmpty ? (
    <VBox grow={"golden"} justifyContent={"center"}>
      <EmptyState
        illustration={<img src={AutomatedSignatures} alt="dCompany" />}
        title={t("decisions.increaseShareCapital.subscribersStatus.emptySubscribers.header")}
        description={t("decisions.increaseShareCapital.subscribersStatus.emptySubscribers.info")}
        actions={model.isPreferentialRights ? [] : addSubscribersActions}
      />
    </VBox>
  ) : (
    <VBox padding maxWidth={"2xl"} gap={"lg"} container>
      <Box vertical={isMobileView()} justifyContent={"space-between"}>
        {model.isPreferentialRights && (
          <Box>
            {model.isOverSubscriptionAllowed &&
              t("decisions.increaseShareCapital.subscribersStatus.preferentialRight.oversubscribe")}
            {!model.isOverSubscriptionAllowed &&
              t("decisions.increaseShareCapital.subscribersStatus.preferentialRight")}
          </Box>
        )}
        {!model.isPreferentialRights && (
          <VBox justifyContent={"center"}>
            <AvailableSharesOverview
              isFixed={selectedShareType.isFixed}
              minShares={selectedShareType.minNumberOfShares}
              maxShares={selectedShareType.maxNumberOfShares}
              assignedShares={model.totalSubscribedShares}
              remainingShares={model.remainingSharesToSubscribe}
            />
          </VBox>
        )}
        <Box gap={"md"}>
          <LinkButton
            icon={"plus"}
            label={t("subscriber.addCommonSigner")}
            onClick={addSignerForAllSubscribers}
            disabled={isLocked}
          />
          {!model.isPreferentialRights && (
            <VBox justifyContent={"center"}>
              <DropdownMenu
                button={{
                  type: "button",
                  label: t("subscriber.addBuyer"),
                  variant: "secondary",
                  disabled: isLocked,
                }}
                items={[
                  {
                    type: "item",
                    label: t("subscriber.addBuyer.existing"),
                    onSelect: () => model.openAddNewShareholderFromPortfolioDialog(),
                  },
                  {
                    type: "item",
                    label: t("subscriber.addBuyer.newPersonalSubscriber"),
                    onSelect: () => model.openAddNewShareholderDialog({ isCompany: false }),
                  },
                  {
                    type: "item",
                    label: t("subscriber.addBuyer.newCompanySubscriber"),
                    onSelect: () => model.openAddNewShareholderDialog({ isCompany: true }),
                  },
                ]}
              />
            </VBox>
          )}
        </Box>
      </Box>
      <Table hoverable withRowBorders>
        <thead>
          <tr>
            {tableColumns.map(({ name, label, align, width = "auto" }) => (
              <th key={String(name)} style={{ width }} align={align || "right"}>
                <>{label}</>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {model.subscribers.map((subscriber) => {
            const subscription = subscriber.getSubscriptionForShareType(
              selectedShareType.sharetypeId,
            );
            if (!subscription) {
              return null;
            }
            return (
              <tr key={subscriber.subscriberid}>
                <td
                  align={"left"}
                  className={
                    subscription.numberOfShares.toNumberOrZero() === 0
                      ? "opacity-dimmed"
                      : undefined
                  }
                >
                  {subscriber.nameField.validatedValue}
                </td>
                <td align={"right"} style={{ padding: "5px 10px 5px 10px" }}>
                  <MTextInput
                    testId={"numberOfShares"}
                    model={subscription.numberOfShares}
                    disabled={model.isPreferentialRights || isLocked}
                    onChange={(value) => {
                      model.setDirty();
                      subscription.numberOfShares.setInputValue(value);
                    }}
                    align={"center"}
                  />
                </td>
                <td align={"right"}>
                  <MTextInput
                    model={subscription.subscriptionPrice}
                    testId={"subscriptionPrice"}
                    disabled={
                      model.isPreferentialRights ||
                      isLocked ||
                      !selectedShareType.isIndividualSharePrice
                    }
                    onChange={(value) => {
                      model.setDirty();
                      subscription.subscriptionPrice.setInputValue(value);
                    }}
                    align={"center"}
                  />
                </td>
                <td align={"right"}>{formatNumber(subscription.totalPrice)}</td>
                <td align={"right"}>
                  {formatNumber(
                    subscriber.getCurrentNumberOfSharesForShareType(selectedShareType.sharetypeId),
                  )}
                </td>
                <td align={"right"} style={{ whiteSpace: "nowrap" }}>
                  {formatPercent(
                    percentOf(
                      subscriber.getCurrentNumberOfSharesForShareType(
                        selectedShareType.sharetypeId,
                      ),
                      model.currentTotalSharesForShareType,
                    ),
                  )}
                </td>
                <td align={"right"}>
                  {formatNumber(
                    subscriber.getNewNumberOfSharesForShareType(selectedShareType.sharetypeId),
                  )}
                </td>
                <td align={"right"} style={{ whiteSpace: "nowrap" }}>
                  {formatPercent(
                    percentOf(
                      subscriber.getNewNumberOfSharesForShareType(selectedShareType.sharetypeId),
                      model.newTotalSharesForShareType,
                    ),
                  )}
                </td>
                <td align={"center"} onClick={() => model.showSubscriberSignerDialog(subscriber)}>
                  <LinkButton label={t(subscriberSignerLabel(subscriber))} />
                </td>
                <td align={"right"}>
                  <DropdownMenu
                    button={t("options")}
                    items={compact([
                      {
                        type: "item",
                        label: t("subscriber.manageSigners"),
                        onSelect: () => model.showSubscriberSignerDialog(subscriber),
                      },
                      !model.isPreferentialRights &&
                        !isLocked && {
                          type: "item",
                          label: t("delete"),
                          color: "error",
                          onSelect: () => model.deleteSubscriber(subscriber),
                        },
                    ])}
                  />
                </td>
              </tr>
            );
          })}
          <tr>
            <td align={"left"}>
              <strong>{t("total")}</strong>
            </td>
            <td align={"center"}>{formatNumber(model.totalSubscribedShares)}</td>
            <td align={"right"}></td>
            <td align={"right"}>{formatNumber(model.totalSubscribedPrice)}</td>
            <td align={"right"}>{formatNumber(model.sumOfCurrentSharesForSubscribers)}</td>
            <td align={"right"} style={{ whiteSpace: "nowrap" }}>
              {formatPercent(
                percentOf(
                  model.sumOfCurrentSharesForSubscribers,
                  model.currentTotalSharesForShareType,
                ),
              )}
            </td>
            <td align={"right"}>{formatNumber(model.sumOfNewSharesForSubscribers)}</td>
            <td align={"right"} style={{ whiteSpace: "nowrap" }}>
              {formatPercent(
                percentOf(model.sumOfNewSharesForSubscribers, model.newTotalSharesForShareType),
              )}
            </td>
            <td align={"right"}></td>
            <td align={"right"}></td>
          </tr>
        </tbody>
      </Table>
    </VBox>
  );
});

const SubscriberStatusNominalValueBody: FC<{
  model: SubscribersStatusForNominalValueModel;
  addSignerForAllSubscribers: () => void;
  isLocked: boolean;
}> = observer(({ model, addSignerForAllSubscribers, isLocked }) => {
  const { t } = useTranslation();
  const { formatNumber, formatPercent } = useLocaleFormat();

  const tableColumns: TableColumn<SubscriberStatusIssueRow>[] = [
    {
      name: "title",
      label: t("name"),
      align: "left",
      width: "20%",
      notSorted: true,
      getValue: (entry) => entry.title ?? <strong>{t("total")}</strong>,
    },

    {
      name: "subscriptionPrice",
      label: t("decisions.increaseShareCapital.subscribersStatus.contributionPerShare"),
      width: "20%",
      getValue: (value) => formatNumber(value.subscriptionPrice),
    },
    {
      name: "currentShares",
      label: t("decisions.increaseShareCapital.subscribersStatus.currentShares"),
      width: "20%",
      getValue: (value) => formatNumber(value.currentShares),
    },
    {
      name: "currentOwnership",
      label: t("decisions.increaseShareCapital.subscribersStatus.currentOwnership"),
      width: "10%",
      getValue: (value) => formatPercent(value.currentOwnership),
    },
    {
      name: "totalPrice",
      label: t("decisions.increaseShareCapital.subscribersStatus.totalPrice"),
      width: "20%",
      getValue: (value) => formatNumber(value.totalPrice),
    },
    {
      name: "signers",
      label: t("decisions.increaseShareCapital.subscribersStatus.signers"),
      align: "center",
      width: "5%",
      getValue: (entry) =>
        entry.subscriber && (
          <LinkButton
            label={t(subscriberSignerLabel(entry.subscriber))}
            onClick={() => entry.subscriber && model.showSubscriberSignerDialog(entry.subscriber)}
          />
        ),
    },
    {
      name: "actions",
      label: "",
      width: "5%",
      getValue: (entry) =>
        entry.subscriber && (
          <DropdownMenu
            button={t("options")}
            items={[
              {
                type: "item",
                label: t("subscriber.manageSigners"),
                onSelect: () =>
                  entry.subscriber && model.showSubscriberSignerDialog(entry.subscriber),
              },
            ]}
          />
        ),
    },
  ];

  return (
    <VBox padding maxWidth={"2xl"} gap={"lg"} container>
      <Box justifyContent={"end"}>
        <LinkButton
          icon={"plus"}
          label={t("subscriber.addCommonSigner")}
          onClick={addSignerForAllSubscribers}
          disabled={isLocked}
        />
      </Box>
      <DataTable
        hoverable
        withRowBorders
        tableColumns={tableColumns}
        tableData={model.toTableData}
        getId={(row) => row.id}
      />
    </VBox>
  );
});

const AvailableSharesOverview: FC<{
  isFixed: boolean;
  maxShares: number;
  minShares: number;
  assignedShares: number;
  remainingShares: number;
  totalPrice?: number;
}> = observer(({ isFixed, maxShares, minShares, assignedShares, remainingShares, totalPrice }) => {
  const { t } = useTranslation();
  const { formatNumber, formatPrice } = useLocaleFormat();
  return (
    <Box gap={false}>
      {isFixed && (
        <Box borderRight paddingRight={"md"}>
          {t("subscriber.availableSharesOverView.available", {
            shares: formatNumber(maxShares),
          })}
        </Box>
      )}
      {!isFixed && (
        <>
          <Box borderRight paddingRight={"md"}>
            {t("subscriber.availableSharesOverView.maxSharesAvailable", {
              shares: formatNumber(maxShares),
            })}
          </Box>
          <Box borderRight paddingX={"md"}>
            {t("subscriber.availableSharesOverView.minSharesAvailable", {
              shares: formatNumber(minShares),
            })}
          </Box>
        </>
      )}
      <Box borderRight paddingX={"md"}>
        {t("subscriber.availableSharesOverView.assigned", {
          shares: formatNumber(assignedShares),
        })}
      </Box>
      <Box
        borderRight={totalPrice !== undefined}
        paddingX={"md"}
        fg={remainingShares < 0 ? "error" : "default"}
      >
        {t(
          isFixed
            ? "subscriber.availableSharesOverView.remaining"
            : "subscriber.availableSharesOverView.available",
          {
            shares: formatNumber(remainingShares),
          },
        )}
      </Box>
      {totalPrice !== undefined && (
        <Box paddingX={"md"}>
          {t("subscriber.availableSharesOverView.totalPrice", {
            totalPrice: formatPrice(CURRENCY_NOK, totalPrice),
          })}
        </Box>
      )}
    </Box>
  );
});

export const AddNewShareholderDialog: FC<{
  model: SubscriberForIssueModel;
  searchCompany: SearchCompanyModel;
  onSubmit: () => void;
  onClose: () => void;
}> = observer(({ model, searchCompany, onSubmit, onClose }) => {
  const { t } = useTranslation();

  return (
    <>
      <Dialog
        title={t("subscriber.add.newShareholder.title")}
        submitLabel={t("add")}
        onSubmit={onSubmit}
        onCancel={onClose}
        maxWidth={"sm"}
      >
        <VBox gap={"md"} paddingY={"md"}>
          <strong>{t("subscriber.add.newShareholder.heading")}</strong>
          <RadioButtonGroup
            horizontal
            options={[true, false]}
            selected={model.isCompany}
            idFn={(v) => String(v)}
            labelFn={(v) =>
              v
                ? t("subscriber.add.newShareholder.type.company")
                : t("subscriber.add.newShareholder.type.person")
            }
            onChange={(_e, v) => model.setIsCompany(v)}
          />
          <Select
            label={t("subscriber.add.newShareholder.nationality")}
            {...model.nationalitySelect}
          />
          {model.isCompany ? (
            <>
              <SelectCompanyComponent model={searchCompany} />
              <MTextInput
                model={model.contactName}
                label={t("subscriber.add.newShareholder.contactPerson")}
              />
              <MTextInput model={model.email} label={t("email")} />
            </>
          ) : (
            <>
              <MTextInput
                model={model.nameField}
                label={t("subscriber.add.newShareholder.name.person")}
                placeholder={t("subscriber.add.newShareholder.name.person.placeholder")}
              />
              <MTextInput
                model={model.nationalId}
                label={t("subscriber.add.newShareholder.nationalId")}
              />
              <MTextInput model={model.email} label={t("email")} />
            </>
          )}
          <Alert type={"info"} message={t("subscriber.add.newShareholder.info")} />
        </VBox>
      </Dialog>
    </>
  );
});

export const SubscriberDialog = observer(function ({
  selectedSubscriber,
  dialogState,
  close,
  isLocked,
  setDirty,
}: {
  selectedSubscriber: SubscriberForIssueModel | SubscriberForIncreaseNominalValueModel;
  dialogState: SubscriberDialogState;
  close: () => void;
  isLocked: boolean;
  setDirty?: () => void;
}) {
  switch (dialogState.type) {
    case "addSigners":
      return (
        <AddSubscriberSignersDialog
          selectedSubscriber={selectedSubscriber}
          close={close}
          isLocked={isLocked}
          setDirty={setDirty}
        />
      );
    case "deleteSubscriber":
      if (selectedSubscriber instanceof SubscriberForIncreaseNominalValueModel) return null;
      return (
        <DeleteSubscriberDialog
          close={close}
          submit={dialogState.submit}
          selectedSubscriber={selectedSubscriber}
        />
      );
    case "addContributionInKind":
      return (
        <AddContributionInKindDialog
          selectedSubscriber={selectedSubscriber}
          model={dialogState.model}
          onClose={close}
        />
      );
    case "addDebtContribution":
      return (
        <AddDebtContributionDialog
          selectedSubscriber={selectedSubscriber}
          model={dialogState.model}
          onClose={close}
        />
      );
    default:
      return null;
  }
});

const DeleteSubscriberDialog: FC<{
  selectedSubscriber: SubscriberForIssueModel;
  submit: (value: SubscriberForIssueModel) => void;
  close: () => void;
}> = observer(({ selectedSubscriber, submit, close }) => {
  const { t } = useTranslation();
  return (
    <>
      <Dialog
        title={t("subscriber.delete.title")}
        submitLabel={t("delete")}
        onSubmit={() => submit(selectedSubscriber)}
        onCancel={close}
      >
        <VBox>
          {t("subscriber.delete.description", {
            name: selectedSubscriber.nameField.validatedValue,
          })}
        </VBox>
      </Dialog>
    </>
  );
});

const AddSubscriberSignersDialog: FC<{
  selectedSubscriber: SubscriberForIssueModel | SubscriberForIncreaseNominalValueModel;
  close: () => void;
  isLocked: boolean;
  setDirty?: () => void;
}> = observer(({ selectedSubscriber, close, isLocked, setDirty }) => {
  const { t } = useTranslation();
  return (
    <>
      <Dialog
        title={t("subscriber.manageSigners.title", {
          name: selectedSubscriber.nameField.validatedValue,
        })}
        cancelLabel={t("done")}
        onCancel={close}
      >
        <VBox>
          {selectedSubscriber.signers.map((signer) => (
            <Box key={signer.id}>
              <MTextInput
                placeholder={t("name")}
                model={signer.name}
                onChange={(value) => {
                  setDirty?.();
                  signer.name.setInputValue(value);
                }}
              />
              <MTextInput
                placeholder={t("email")}
                model={signer.email}
                onChange={(value) => {
                  setDirty?.();
                  signer.email.setInputValue(value);
                }}
              />
              <Box alignItems={"center"}>
                <IconButton
                  tooltip={t("remove")}
                  onClick={() => {
                    setDirty?.();
                    selectedSubscriber.removeSigner(signer);
                  }}
                  disabled={isLocked}
                  icon={"trash"}
                  color={"error"}
                />
              </Box>
            </Box>
          ))}
          <Box>
            <LinkButton
              label={t("add")}
              icon={"plus"}
              onClick={() => {
                setDirty?.();
                selectedSubscriber.addSigner();
              }}
              disabled={isLocked}
            />
          </Box>
        </VBox>
      </Dialog>
    </>
  );
});

const SelectCompanyComponent: FC<{
  model: SearchCompanyModel;
}> = observer(({ model }) => {
  const { t } = useTranslation();

  return (
    <>
      <Autocomplete
        name={"addNewCompanySubscriber"}
        label={t("subscriber.add.newShareholder.name.company")}
        placeholder={t("subscriber.add.newShareholder.name.company.placeholder")}
        value={model.selectedCompany ?? null}
        items={model.suggestedCompanies}
        onSelect={model.onSelectCompany}
        idFn={(item) =>
          item.organizationnumber === "" ? item.name : `${item.name} (${item.organizationnumber})`
        }
        labelFn={(item) =>
          item.organizationnumber === "" ? item.name : `${item.name} (${item.organizationnumber})`
        }
        inputValue={model.companyName.inputValue}
        error={!model.companyName.isValid}
        message={when(model.companyName.error, t)}
        onInputValueChange={model.onInputValueChange}
        onInputBlur={model.companyName.commit}
        onClear={model.onClearCompany}
      />
      <MTextInput
        model={model.orgNo}
        label={t("subscriber.add.newShareholder.orgno")}
        disabled={model.disableOrgNo}
      />
    </>
  );
});

const AddSignerForAllSubscribersDialog: FC<{
  model: { nameField: StringFieldModel; emailField: StringFieldModel };
  submit: () => void;
  close: () => void;
}> = observer(({ model, submit, close }) => {
  const { t } = useTranslation();

  return (
    <Dialog
      title={t("subscriber.addCommonSigner")}
      submitLabel={t("add")}
      onSubmit={submit}
      onCancel={close}
    >
      <Box>
        <MTextInput autofocus label={t("name")} model={model.nameField} />
        <MTextInput label={t("email")} model={model.emailField} />
      </Box>
    </Dialog>
  );
});
